import { Canvas, Circle, Path, Point, Rect, Triangle, Polyline } from 'fabric';

import {
  OBJECT_SOURCE_KEY,
  MAX_ZOOM,
  MIN_SHAPE_SIZE,
  MIN_ZOOM,
  ZOOM_STEP,
  OBJECT_SOURCE_KEY_QUESTION,
  DRAW_OBJECT_TYPE_IMAGE,
} from './draw-canvas.constants';
import {
  customiseControlStyles,
  customiseShapeControlPoints,
  initialiseDeleteControl,
} from './logic/draw-controls';
import { isArtboard } from './logic/is-artboard';
import { isSquare } from './logic/is-square';
import { isCircle } from './logic/is-circle';
import { isTriangle } from './logic/is-triangle';
import { isRectangle } from './logic/is-rectangle';
import { isLine } from './logic/is-line';

type BoundingBox = { left: number; top: number; width: number; height: number };

/**
 * Computes the union bounding box enclosing every supplied bounding rect (e.g.
 * from `canvas.getObjects().map(o => o.getBoundingRect())`). Returns null for an
 * empty list so callers can no-op on an empty canvas. Strokes drawn outside the
 * image area grow the box to enclose them.
 */
export function getObjectsBoundingBox(
  rects: BoundingBox[]
): BoundingBox | null {
  if (!rects.length) {
    return null;
  }

  const left = Math.min(...rects.map(rect => rect.left));
  const top = Math.min(...rects.map(rect => rect.top));
  const right = Math.max(...rects.map(rect => rect.left + rect.width));
  const bottom = Math.max(...rects.map(rect => rect.top + rect.height));

  return { left, top, width: right - left, height: bottom - top };
}

export function resetZoomAndViewport(canvas: Canvas, originalWidth: number) {
  const ratio = (originalWidth - (canvas.width || 0)) / originalWidth;
  const newZoom = 1 - ratio;
  canvas.setViewportTransform([newZoom, 0, 0, newZoom, 0, 0]);
  return newZoom;
}

export function fitContentToCanvas(
  canvas: Canvas,
  artboard: Rect,
  instructionHeight?: number
) {
  const canvasHeight = canvas?.height || 0;
  const canvasWidth = canvas?.width || 0;
  const instructionHeightWithMargin = instructionHeight
    ? instructionHeight + 30
    : 0;

  const boundingRect = artboard.getBoundingRect();
  const boundingCenter = new Point(
    boundingRect.left + boundingRect.width / 2,
    boundingRect.top + boundingRect.height / 2
  );

  const scaleFactor =
    Math.min(
      canvasWidth / boundingRect.width,
      (canvasHeight - instructionHeightWithMargin) / boundingRect.height
    ) * 1;

  canvas.zoomToPoint(boundingCenter, scaleFactor);

  const canvasCenter = new Point(
    canvasWidth / 2,
    (canvasHeight + instructionHeightWithMargin) / 2
  );
  canvas.absolutePan(
    new Point(
      boundingCenter.x * scaleFactor - canvasCenter.x,
      boundingCenter.y * scaleFactor - canvasCenter.y
    )
  );

  canvas.requestRenderAll();
}

export function increaseZoom(zoomLevel: number) {
  if (zoomLevel < MAX_ZOOM) {
    return Math.min(zoomLevel + ZOOM_STEP, MAX_ZOOM);
  }
  return zoomLevel;
}

export function decreaseZoom(zoomLevel: number) {
  if (zoomLevel > MIN_ZOOM) {
    return Math.max(zoomLevel - ZOOM_STEP, MIN_ZOOM);
  }
  return zoomLevel;
}

export function removeAllObjects(canvas: Canvas | null) {
  canvas?.off('object:removed');
  canvas?.getObjects().forEach(ob => {
    if (
      ob.type !== DRAW_OBJECT_TYPE_IMAGE &&
      !isArtboard(ob) &&
      !(
        OBJECT_SOURCE_KEY in ob &&
        ob[OBJECT_SOURCE_KEY] === OBJECT_SOURCE_KEY_QUESTION
      )
    ) {
      canvas?.remove(ob);
    }
  });
}

export async function onPathCreated(canvas: Canvas, path: Path) {
  customiseControlStyles(path);
  customiseShapeControlPoints(path);
  initialiseDeleteControl(canvas, path);
}

export function getTouchEvent(evt: any) {
  if (evt.e.touches?.length) {
    return evt.e.touches[0];
  }
  return evt.e;
}

export function dragAddingShape(
  pointer: Point,
  canvas: Canvas,
  currentShape: Circle | Triangle | Rect | Polyline,
  mouseDragPointStart: Point
) {
  if (isLine(currentShape)) {
    const polyline = currentShape as Polyline;
    polyline.objectCaching = false;

    // get the left and top of a box if it was drawn around the line
    const left = Math.min(mouseDragPointStart.x, pointer.x);
    const top = Math.min(mouseDragPointStart.y, pointer.y);

    // set points relative to the line's position
    // which enables the user to drag the line in any direction
    polyline.set({
      left,
      top,
      points: [
        { x: mouseDragPointStart.x - left, y: mouseDragPointStart.y - top },
        { x: pointer.x - left, y: pointer.y - top },
      ],
    });
    polyline.setDimensions();
  } else {
    if (mouseDragPointStart.x > pointer.x) {
      currentShape?.set({ left: Math.abs(pointer.x) });
    }
    if (mouseDragPointStart.y > pointer.y) {
      currentShape?.set({ top: Math.abs(pointer.y) });
    }

    if (isCircle(currentShape)) {
      const radius = Math.abs(pointer.x - mouseDragPointStart.x);
      currentShape?.set({
        radius: Math.max(radius, MIN_SHAPE_SIZE),
      });
    } else if (isTriangle(currentShape) || isRectangle(currentShape)) {
      const width = Math.abs(mouseDragPointStart.x - pointer.x);
      const height = Math.abs(mouseDragPointStart.y - pointer.y);

      // When dragging a square, we need to set the width and height to the same value
      if (isSquare(currentShape)) {
        const size = Math.max(width, height);
        currentShape?.set({
          width: Math.max(size, MIN_SHAPE_SIZE),
          height: Math.max(size, MIN_SHAPE_SIZE),
        });
      } else {
        currentShape?.set({
          width: Math.max(width, MIN_SHAPE_SIZE),
          height: Math.max(height, MIN_SHAPE_SIZE),
        });
      }
    }
  }

  canvas.renderAll();
}

export function getControlCursor(control: string) {
  switch (control) {
    case 'tr':
      return 'ne-resize';
    case 'tl':
      return 'nw-resize';
    case 'bl':
      return 'sw-resize';
    case 'br':
      return 'se-resize';
    case 'ml':
    case 'mr':
      return 'ew-resize';
    case 'mt':
    case 'mb':
      return 'ns-resize';
    case 'mtr':
      return 'ne-resize';
    default:
  }

  return 'pointer';
}
